import AddButton from "components/Button/AddButton";
import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DeployedYear, DropdownMonthList, DropdownPaymentType, DropdownStatusList, EmptyString, FormTypeAdd, PageSize, StatusCodeActive, StatusCodeDelete, ViewDateFormat } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import Dropdown from "components/Input/Dropdown";
import { GetMonth, GetPaymentType, GetStatus, money } from "utils/helpers";
import RevisionIcon from "assets/icon/RevisionIcon";
import moment from "moment";
import { MainBodyCard } from "components/Cards/MainBodyCard";
import { PagesTitle } from "components/Cards/PagesTitle";
import { FilterCard } from "components/Cards/FilterCard";
import DataTable from "components/DataTable";
import Pagination from "components/Input/PaginationButton";
import { PaymentService } from "services/PaymentService";
import { GridEditButton } from "components/Button/GridEditButton";
import { GridDeleteButton } from "components/Button/GridDeleteButton";
import { useModal } from "utils/context/ModalContext";
import { GridPrintButton } from "components/Button/GridPrintButton";
import { PaymentInvoiceService } from "reportServices/PaymentInvoiceService";
import { ReportsService } from "services/ReportsService";

export default function Payment(){
    let navigate = useNavigate()

    const firstLoad = useRef(true)
    const debounceRef = useRef(null)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()

    const { fetchPaymentList, fetchDeletePayment, fetchPaymentById } = PaymentService()
    const { fetchPaymentInvoice } = ReportsService()

    // FILTER
    const [tenantName, setTenantName] = useState(EmptyString)
    const [paymentPeriodeMonth, setPaymentPeriodeMonth] = useState(moment(new Date()).format("M"))
    const [paymentPeriodeYear, setPaymentPeriodeYear] = useState(moment(new Date()).format("yyyy"))
    const [yearOptions, setYearOptions] = useState([])
    const [paymentType, setPaymentType] = useState(EmptyString)
    const [status, setStatus] = useState(DropdownStatusList[1].value)

    // DATA
    const [paymentList, setPaymentList] = useState([])

    // GRID
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "tenantName",
            header: "Nama Tenant",
            render: (row) => row.tenantName.toUpperCase()
        },
        {
            key: "paymentDate",
            header: "Payment Date",
            render: (row) => moment(row.paymentDate).format('DD MMM YYYY')
        },
        {
            key: "paymentType",
            header: "Payment Type",
            render: (row) => GetPaymentType(row.paymentType)
        },
        {
            key: "paymentPeriode",
            header: "Payment Periode",
            render: (row) => GetMonth(row.paymentPeriodeMonth) + ' ' + row.paymentPeriodeYear
        },
        {
            key: "roomNumber",
            header: "Room Number",
        },
        {
            key: "amount",
            header: "Amount",
            render: (row) => 'Rp ' + money(row.amount)
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridPrintButton onClick={() => printBtnClick(row.paymentId)} size='w-3' />
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.paymentId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.paymentId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]

    const handlePageChange = async (newIndex) => {
        try {
            showLoading()

            const res = await fetchPaymentList(
                tenantName,
                paymentPeriodeMonth,
                paymentPeriodeYear,
                paymentType,
                status,
                PageSize,
                newIndex
            )

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setPaymentList(res)
            setTotalPages(total)
            setPageIndex(newIndex)
        } finally {
            hideLoading()
        }
    }

    const addBtnClick = () => {
        navigate("/payment/form?paymentId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (paymentId) => {
        navigate("/payment/form?paymentId=" + paymentId + "&formType=Edit")
    }
    const deleteBtnClick = async (paymentId, status) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        try {
            showLoading()

            const res = await fetchDeletePayment(paymentId, status)
            if (!res) return

            showSuccess({
                onSuccess: async () => {
                    await handlePageChange(pageIndex)
                }
            })
        } finally {
            hideLoading()
        }
    }
    const printBtnClick = async (paymentId) => {
        const res = await fetchPaymentInvoice(paymentId)
        if (!res) return

        const data = res.data

        PaymentInvoiceService(data);
    }

    function fGetYear(){
        let deployedYear = parseFloat(DeployedYear)
        let thisYear = parseFloat(moment(new Date()).format("yyyy"))
        let tempArray = []

        if (thisYear > deployedYear){
            for(let i = deployedYear; i < (thisYear + 3); i++){
                let tempArray = []
                tempArray.push({label: String(i), value: String(i)})                
            }
        }else{
            for(let i = deployedYear; i < (deployedYear + 3); i++){                
                tempArray.push({label: String(i), value: String(i)})
            }
        }

        setYearOptions(tempArray)
    }

    useEffect(() => {
        const onLoad = async () => {
            fGetYear()

            if (firstLoad.current) {
                handlePageChange(0)

                firstLoad.current = false
            }
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[tenantName, paymentPeriodeMonth, paymentPeriodeYear, paymentType, status])

    return(
        <MainBodyCard>
            <PagesTitle title='Payment' />

            <div className="flex flex-col sm:flex-row gap-5 mt-4">
                <FilterCard title='Tenant Name' width='w-full sm:w-64'>
                    <input className="input" value={tenantName} onChange={(e) => setTenantName(e.target.value.toUpperCase())} />
                </FilterCard>

                <FilterCard title='Payment Periode' width='w-full sm:w-64'>
                    <div className="flex gap-2 justify-between w-full">
                        <Dropdown className="w-7/12" value={paymentPeriodeMonth} options={DropdownMonthList} onChange={(e) => { setPaymentPeriodeMonth(e.value) }} placeholder={"All"} />
                        <Dropdown className={`w-5/12`} value={paymentPeriodeYear} options={yearOptions} onChange={(e) => { setPaymentPeriodeYear(e.value) }} placeholder={"All"} />
                    </div>    
                </FilterCard>

                <FilterCard title='Payment Type' width='w-full sm:w-32'>
                    <Dropdown className='w-full' value={paymentType} options={DropdownPaymentType} onChange={(e) => { setPaymentType(e.value) }} placeholder={"All"} />
                </FilterCard>

                <FilterCard title='Status' width='w-full sm:w-32'>
                    <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => {setStatus(e.value)}} placeholder={"All"} />
                </FilterCard>
            </div>

            <div className="mt-4 flex w-full justify-end">
                <AddButton onClick={addBtnClick} title='Tambah Payment' />
            </div>
           
            <div className="mt-4 hidden sm:block">
                <DataTable
                    columns={columns}
                    data={paymentList?.data?.listData ?? []}
                    footer={
                        <tr className="h-10">
                            <td colSpan={6} className="text-right pr-2">Total</td>
                            <td colSpan={2} className="text-left pr-2">{"Rp " + money(String(Math.floor(paymentList?.data?.totalPayment)))}</td>
                        </tr>
                    }
                />
            </div>

            <div className="mt-5 sm:hidden flex flex-col gap-2">
                {paymentList?.data?.listData?.map((data, i) => {
                    return(
                        <div className="border rounded-md hover:bg-slate-100 bg-white py-2 px-2 flex justify-between" key={ i }>
                            <div className="text-sm">
                                <div>
                                    <span>{ data.tenantName.toUpperCase() + " - " + data.roomNumber }</span>
                                </div>                                
                                <div>
                                    <span className="font-semibold">{ "Rp " + money(data.amount) }</span>
                                </div>
                                <div className="mt-1 flex gap-2">
                                    <button className="bg-black px-3 py-1 rounded-md flex gap-2" onClick={() => editBtnClick(data.paymentId)}>
                                        <EditIcon className="w-4 fill-white" />
                                        <span className="text-white">Edit</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.paymentId, data.status)}>
                                        <TrashIcon className="w-4 fill-white" />
                                        <span className="text-white">Delete</span>
                                    </button>
                                    <button className={`bg-black px-3 py-1 rounded-md flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.paymentId, data.status)}>
                                        <RevisionIcon className="w-4 fill-white" />
                                        <span className="text-white">Undelete</span>
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-col text-right text-sm">
                                <div>
                                    <span>{ moment(data.paymentDate).format(ViewDateFormat) }</span>
                                </div>
                                <div>
                                    <span>{ GetMonth(data.paymentPeriodeMonth) + ' ' + data.paymentPeriodeYear }</span>
                                </div>
                                <div className="flex h-full items-center justify-end">
                                    <span>{ GetPaymentType(String(data.paymentType)) }</span>
                                </div>
                            </div>
                            {/* <td className="px-2">
                                <span>{ moment(data.paymentDate).format(ViewDateFormat) }</span>
                            </td>
                            <td className="px-2">
                                <span>{ GetPaymentType(String(data.paymentType)) }</span>
                            </td>
                            <td className="px-2">
                                <span>{ GetMonth(data.paymentPeriodeMonth) + ' ' + data.paymentPeriodeYear }</span>
                            </td>
                            <td className="px-2">
                                
                            </td>
                            <td className="px-2 hidden sm:table-cell">
                                <span>{ GetStatus(data.status) }</span>
                            </td>
                            <td className="flex flex-col sm:flex-row justify-center py-2 gap-2 px-2">
                                <button className="button flex gap-2" onClick={() => editBtnClick(data.paymentId)}>
                                    <EditIcon className="w-4 fill-white" />
                                </button>
                                <button className={`button flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.paymentId, data.status)}>
                                    <TrashIcon className="w-4 fill-white" />
                                </button>
                                <button className={`button flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.paymentId, data.status)}>
                                    <RevisionIcon className="w-4 fill-white" />
                                </button>
                            </td> */}
                        </div>
                    )
                })}
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}